/**
 * SolutionPanel Component
 * 
 * Displays the Product Solution panel with two sections:
 * - Solution Inputs (top): Things needed to use the product
 * - Solution Benefits (bottom): Value propositions the product provides
 * 
 * Each item can be linked to corresponding situation elements.
 */

import React, { useState } from 'react';
import { 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Checkbox,
  Tooltip,
  Empty,
  message 
} from 'antd';
import {
  AppstoreOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ImportOutlined,
  GiftOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { 
  ProductSolution, 
  SolutionInput, 
  SolutionBenefit,
  ConnectionLink,
} from '../../types/marketFit';
import { useMarketFit } from '../../contexts/MarketFitContext';
import styles from './SolutionPanel.module.css';

interface SolutionPanelProps {
  solution: ProductSolution | null;
  inputLinks: ConnectionLink[];
  benefitLinks: ConnectionLink[];
  onInputSelect?: (inputId: string) => void;
  onBenefitSelect?: (benefitId: string) => void;
  selectedInputId?: string | null;
  selectedBenefitId?: string | null;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface InputItemProps {
  input: SolutionInput;
  link?: ConnectionLink;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const InputItem: React.FC<InputItemProps> = ({
  input,
  link,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const getLinkStatus = () => {
    if (!link) return { class: styles.itemUnlinked, icon: null };
    if (link.matchType === 'full') return { 
      class: styles.itemLinked, 
      icon: <CheckCircleOutlined className={styles.linkIconFull} /> 
    };
    if (link.matchType === 'partial') return { 
      class: styles.itemPartial, 
      icon: <ExclamationCircleOutlined className={styles.linkIconPartial} /> 
    };
    return { class: styles.itemUnlinked, icon: null };
  };

  const linkStatus = getLinkStatus();

  return (
    <div 
      className={`${styles.item} ${linkStatus.class} ${isSelected ? styles.itemSelected : ''}`}
      onClick={onSelect}
    >
      <div className={styles.itemContent}>
        <ImportOutlined className={styles.itemIcon} />
        <div className={styles.itemInfo}>
          <span className={styles.itemName}>{input.name}</span>
          {input.description && (
            <span className={styles.itemDescription}>{input.description}</span>
          )}
        </div>
      </div>
      <div className={styles.itemMeta}>
        {linkStatus.icon}
        <span className={`${styles.itemTag} ${input.isRequired ? styles.itemTagRequired : styles.itemTagOptional}`}>
          {input.isRequired ? 'Required' : 'Optional'}
        </span>
        <div className={styles.itemActions}>
          <Tooltip title="Edit">
            <button 
              className={styles.itemActionButton}
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button 
              className={`${styles.itemActionButton} ${styles.itemActionButtonDelete}`}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

interface BenefitItemProps {
  benefit: SolutionBenefit;
  links: ConnectionLink[];
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const BenefitItem: React.FC<BenefitItemProps> = ({
  benefit,
  links,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const linkedCount = links.filter(l => l.fromId === benefit.id).length;
  
  const getLinkStatus = () => {
    if (linkedCount === 0) return { class: styles.itemUnlinked, icon: null };
    const hasFullMatch = links.some(l => l.fromId === benefit.id && l.matchType === 'full');
    if (hasFullMatch) return { 
      class: styles.itemLinked, 
      icon: <CheckCircleOutlined className={styles.linkIconFull} /> 
    };
    return { 
      class: styles.itemPartial, 
      icon: <ExclamationCircleOutlined className={styles.linkIconPartial} /> 
    };
  };

  const linkStatus = getLinkStatus();

  return (
    <div 
      className={`${styles.item} ${linkStatus.class} ${isSelected ? styles.itemSelected : ''}`}
      onClick={onSelect}
    >
      <div className={styles.itemContent}>
        <GiftOutlined className={styles.itemIcon} />
        <div className={styles.itemInfo}>
          <span className={styles.itemName}>{benefit.name}</span>
          {benefit.valueProposition && (
            <span className={styles.itemDescription}>{benefit.valueProposition}</span>
          )}
        </div>
      </div>
      <div className={styles.itemMeta}>
        {linkStatus.icon}
        {linkedCount > 0 && (
          <Tooltip title={`Linked to ${linkedCount} need${linkedCount > 1 ? 's' : ''}`}>
            <span className={styles.linkCount}>
              <LinkOutlined /> {linkedCount}
            </span>
          </Tooltip>
        )}
        <div className={styles.itemActions}>
          <Tooltip title="Edit">
            <button 
              className={styles.itemActionButton}
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button 
              className={`${styles.itemActionButton} ${styles.itemActionButtonDelete}`}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const SolutionPanel: React.FC<SolutionPanelProps> = ({
  solution,
  inputLinks,
  benefitLinks,
  onInputSelect,
  onBenefitSelect,
  selectedInputId,
  selectedBenefitId,
}) => {
  const { 
    addSolutionInput, 
    updateSolutionInput, 
    removeSolutionInput,
    addSolutionBenefit,
    updateSolutionBenefit,
    removeSolutionBenefit,
  } = useMarketFit();

  const [inputModalVisible, setInputModalVisible] = useState(false);
  const [benefitModalVisible, setBenefitModalVisible] = useState(false);
  const [editingInput, setEditingInput] = useState<SolutionInput | null>(null);
  const [editingBenefit, setEditingBenefit] = useState<SolutionBenefit | null>(null);
  
  const [inputForm] = Form.useForm();
  const [benefitForm] = Form.useForm();

  // Input handlers
  const handleAddInput = () => {
    setEditingInput(null);
    inputForm.resetFields();
    setInputModalVisible(true);
  };

  const handleEditInput = (input: SolutionInput) => {
    setEditingInput(input);
    inputForm.setFieldsValue(input);
    setInputModalVisible(true);
  };

  const handleDeleteInput = (inputId: string) => {
    Modal.confirm({
      title: 'Delete Input',
      content: 'Are you sure you want to delete this input? Any connection links will also be removed.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        removeSolutionInput(inputId);
        void message.success('Input deleted');
      },
    });
  };

  const handleInputSubmit = () => {
    inputForm.validateFields().then(values => {
      if (editingInput) {
        updateSolutionInput(editingInput.id, values);
        void message.success('Input updated');
      } else {
        addSolutionInput(values);
        void message.success('Input added');
      }
      setInputModalVisible(false);
      inputForm.resetFields();
    }).catch(() => {
      // Validation failed
    });
  };

  // Benefit handlers
  const handleAddBenefit = () => {
    setEditingBenefit(null);
    benefitForm.resetFields();
    setBenefitModalVisible(true);
  };

  const handleEditBenefit = (benefit: SolutionBenefit) => {
    setEditingBenefit(benefit);
    benefitForm.setFieldsValue(benefit);
    setBenefitModalVisible(true);
  };

  const handleDeleteBenefit = (benefitId: string) => {
    Modal.confirm({
      title: 'Delete Benefit',
      content: 'Are you sure you want to delete this benefit? Any connection links will also be removed.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        removeSolutionBenefit(benefitId);
        void message.success('Benefit deleted');
      },
    });
  };

  const handleBenefitSubmit = () => {
    benefitForm.validateFields().then(values => {
      if (editingBenefit) {
        updateSolutionBenefit(editingBenefit.id, values);
        void message.success('Benefit updated');
      } else {
        addSolutionBenefit(values);
        void message.success('Benefit added');
      }
      setBenefitModalVisible(false);
      benefitForm.resetFields();
    }).catch(() => {
      // Validation failed
    });
  };

  const inputs = solution?.inputs ?? [];
  const benefits = solution?.benefits ?? [];

  return (
    <div className={`${styles.panel} ${styles.solutionPanel}`}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          <AppstoreOutlined className={styles.panelIcon} />
          <span>Product Solution</span>
          {solution && <span className={styles.panelBadge}>{solution.name}</span>}
        </div>
      </div>

      <div className={styles.panelContent}>
        {/* Inputs Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <ImportOutlined className={styles.sectionIcon} />
              <span>Solution Inputs</span>
              <span className={styles.sectionCount}>({inputs.length})</span>
            </div>
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />}
              onClick={handleAddInput}
              className={styles.addButton}
            >
              Add
            </Button>
          </div>
          
          <div className={styles.itemList}>
            {inputs.length === 0 ? (
              <Empty 
                description="No inputs defined" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className={styles.emptyState}
              />
            ) : (
              inputs.map(input => (
                <InputItem
                  key={input.id}
                  input={input}
                  link={inputLinks.find(l => l.fromId === input.id)}
                  isSelected={selectedInputId === input.id}
                  onSelect={() => onInputSelect?.(input.id)}
                  onEdit={() => handleEditInput(input)}
                  onDelete={() => handleDeleteInput(input.id)}
                />
              ))
            )}
          </div>
        </div>

        {/* Benefits Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <GiftOutlined className={styles.sectionIcon} />
              <span>Solution Benefits</span>
              <span className={styles.sectionCount}>({benefits.length})</span>
            </div>
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />}
              onClick={handleAddBenefit}
              className={styles.addButton}
            >
              Add
            </Button>
          </div>
          
          <div className={styles.itemList}>
            {benefits.length === 0 ? (
              <Empty 
                description="No benefits defined" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className={styles.emptyState}
              />
            ) : (
              benefits.map(benefit => (
                <BenefitItem
                  key={benefit.id}
                  benefit={benefit}
                  links={benefitLinks}
                  isSelected={selectedBenefitId === benefit.id}
                  onSelect={() => onBenefitSelect?.(benefit.id)}
                  onEdit={() => handleEditBenefit(benefit)}
                  onDelete={() => handleDeleteBenefit(benefit.id)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Input Modal */}
      <Modal
        title={editingInput ? 'Edit Input' : 'Add Input'}
        open={inputModalVisible}
        onOk={handleInputSubmit}
        onCancel={() => setInputModalVisible(false)}
        okText={editingInput ? 'Update' : 'Add'}
        className={styles.formModal}
      >
        <Form form={inputForm} layout="vertical">
          <Form.Item 
            name="name" 
            label="Input Name" 
            rules={[{ required: true, message: 'Please enter input name' }]}
          >
            <Input placeholder="e.g., User Account, API Key, Data File" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea 
              placeholder="Describe what this input is and why it's needed" 
              rows={3}
            />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Select placeholder="Select category" allowClear>
              <Select.Option value="data">Data</Select.Option>
              <Select.Option value="infrastructure">Infrastructure</Select.Option>
              <Select.Option value="credentials">Credentials</Select.Option>
              <Select.Option value="personnel">Personnel</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="isRequired" valuePropName="checked">
            <Checkbox>This input is required</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Benefit Modal */}
      <Modal
        title={editingBenefit ? 'Edit Benefit' : 'Add Benefit'}
        open={benefitModalVisible}
        onOk={handleBenefitSubmit}
        onCancel={() => setBenefitModalVisible(false)}
        okText={editingBenefit ? 'Update' : 'Add'}
        className={styles.formModal}
      >
        <Form form={benefitForm} layout="vertical">
          <Form.Item 
            name="name" 
            label="Benefit Name" 
            rules={[{ required: true, message: 'Please enter benefit name' }]}
          >
            <Input placeholder="e.g., Time Savings, Cost Reduction, Quality Improvement" />
          </Form.Item>
          <Form.Item name="valueProposition" label="Value Proposition">
            <Input.TextArea 
              placeholder="Describe the value this benefit provides to the customer" 
              rows={3}
            />
          </Form.Item>
          <Form.Item name="description" label="Additional Details">
            <Input.TextArea 
              placeholder="Any additional details about this benefit" 
              rows={2}
            />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Select placeholder="Select category" allowClear>
              <Select.Option value="efficiency">Efficiency</Select.Option>
              <Select.Option value="cost">Cost Savings</Select.Option>
              <Select.Option value="quality">Quality</Select.Option>
              <Select.Option value="speed">Speed</Select.Option>
              <Select.Option value="risk">Risk Reduction</Select.Option>
              <Select.Option value="other">Other</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SolutionPanel;
